import { FC } from 'react';
import { CustomButton, Tabs } from '@medtronic/pecc-react-component-library-js';
import { Divider, Link, Modal, Stack, Typography } from '@mui/material';
import { ModalHeader } from '~/components';
import { SettingsCheckIcon, TerminalIcon } from '~/icons';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { modalContainerStyle } from '../styles';
import { PlatformCommands } from './components';
import connectionInstructionsConfig from './config';
import { ConnectionInstructionsType, InstructionsConfig, Platform } from './types';

export interface ConnectionInstructionsProps {
  payload: {
    type?: ConnectionInstructionsType;
    instructionsConfig: InstructionsConfig;
    id: string;
  };
}

const AWS_COMMAND_LINE_REFERENCE = 'https://docs.aws.amazon.com/cli/latest/userguide/getting-started-install.html';
const AWS_SYSTEM_MANAGER =
  'https://docs.aws.amazon.com/systems-manager/latest/userguide/session-manager-working-with-install-plugin.html';

const ConnectionInstructions: FC<ConnectionInstructionsProps> = ({
  payload: { id, type = ConnectionInstructionsType.WORKBENCH, instructionsConfig },
}) => {
  const dispatch = useAppDispatch();
  const { title, description } = connectionInstructionsConfig[type];
  const platformTabs = [
    {
      label: `${Platform.WINDOWS} (Command Prompt/CMD)`,
      content: <PlatformCommands type={type} instructionsConfig={instructionsConfig} platform={Platform.WINDOWS} />,
    },
    {
      label: Platform.LINUX,
      content: <PlatformCommands type={type} instructionsConfig={instructionsConfig} platform={Platform.LINUX} />,
    },
    {
      label: Platform.MAC,
      content: <PlatformCommands type={type} instructionsConfig={instructionsConfig} platform={Platform.MAC} />,
    },
  ];

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  return (
    <Modal open onClose={handleClose} disableAutoFocus>
      <Stack sx={modalContainerStyle}>
        <ModalHeader title={[title]} onClose={handleClose} />
        <Stack p={2} sx={{ overflowY: 'scroll' }}>
          <Stack alignItems="start" justifyContent="center" spacing={2} width="100%">
            <Stack p={2} spacing={2}>
              <Typography variant="buttonTextStandard">{description}</Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1}>
                  <SettingsCheckIcon style={{ width: 36, height: 36 }} />
                  <Stack>
                    <Typography variant="buttonTextStandard">Step 1: Initial Setup and Configuration</Typography>
                    <Typography>
                      Ensure that you have the latest version of AWS installed and that the SSM plugin is also
                      installed.
                    </Typography>
                  </Stack>
                </Stack>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1}>
                    <Typography>1. Make sure you have the latest version of the AWS CLI - </Typography>
                    <Link
                      color="electricBlue.main"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => window.open(AWS_COMMAND_LINE_REFERENCE, '_blank', 'noopener,noreferrer')}
                    >
                      Learn more
                    </Link>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Typography>2. Install the SSM Plugin on your machine - </Typography>
                    <Link
                      color="electricBlue.main"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => window.open(AWS_SYSTEM_MANAGER, '_blank', 'noopener,noreferrer')}
                    >
                      Learn more
                    </Link>
                  </Stack>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TerminalIcon style={{ width: 36, height: 36 }} />
                <Stack>
                  <Typography variant="buttonTextStandard">Step 2: Connection Setup</Typography>
                  <Typography>Input these commands to initiate a tunnel for your remote desktop connection:</Typography>
                </Stack>
              </Stack>
              <Tabs isLeftAlign labelSx={{ textTransform: 'none' }} tabsData={platformTabs} width="100%" />
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="end" p={2}>
          <CustomButton onClick={handleClose}>Ok</CustomButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ConnectionInstructions;
